// src/pages/ERP/SeriLogs/LogViewer.js
import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { spacing } from "@material-ui/system";
import {
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  Box,
  InputAdornment,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
} from "@material-ui/icons";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const StyledTableContainer = styled(TableContainer)`
  margin-top: ${(props) => props.theme.spacing(3)}px;
`;

const LogMessage = styled(TableCell)`
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ControlsContainer = styled(Box)`
  display: flex;
  gap: ${(props) => props.theme.spacing(2)}px;
  margin-bottom: ${(props) => props.theme.spacing(3)}px;
  flex-wrap: wrap;
  align-items: center;
`;

const LogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState("All");

  const API_BASE_URL = process.env.REACT_APP_DOMAIN_API;

  // Debug function untuk testing format
  const debugLogFormat = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/logs/debug`);
      const data = await response.json();
      console.log("Log Format Debug:", data);
    } catch (error) {
      console.error("Debug error:", error);
    }
  };

  useEffect(() => {
    fetchLogs();
    // Uncomment untuk debugging
    // debugLogFormat();
  }, [page, rowsPerPage]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/logs?page=${page + 1}&pageSize=${rowsPerPage}`
      );
      const data = await response.json();
      setLogs(data.logs || []);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching logs:", error);
      setLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const getLogLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case "error":
      case "err":
        return "secondary";
      case "warning":
      case "warn":
      case "wrn":
        return "default";
      case "information":
      case "info":
      case "inf":
        return "primary";
      case "debug":
      case "dbg":
        return "default";
      case "fatal":
      case "fat":
        return "secondary";
      default:
        return "default";
    }
  };

  const getLogLevelVariant = (level) => {
    switch (level?.toLowerCase()) {
      case "error":
      case "err":
      case "fatal":
      case "fat":
        return "default";
      case "warning":
      case "warn":
      case "wrn":
        return "outlined";
      case "information":
      case "info":
      case "inf":
        return "default";
      case "debug":
      case "dbg":
        return "outlined";
      default:
        return "outlined";
    }
  };

  // Normalize log level untuk comparison
  const normalizeLogLevel = (level) => {
    if (!level) return "";
    const normalized = level.toLowerCase().trim();
    switch (normalized) {
      case "err":
        return "error";
      case "wrn":
        return "warning";
      case "inf":
        return "information";
      case "dbg":
        return "debug";
      case "fat":
        return "fatal";
      case "warn":
        return "warning";
      case "info":
        return "information";
      case "trace":
      case "trc":
        return "trace";
      default:
        return normalized;
    }
  };

  // Filter logs berdasarkan search term dan level
  const filteredLogs = logs.filter((log) => {
    // Search filter
    const matchesSearch =
      !searchTerm ||
      log.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.level?.toLowerCase().includes(searchTerm.toLowerCase());

    // Level filter
    let matchesLevel = true;
    if (levelFilter !== "All") {
      const logLevel = normalizeLogLevel(log.level);
      const filterLevel = levelFilter.toLowerCase();
      matchesLevel = logLevel === filterLevel;
    }

    return matchesSearch && matchesLevel;
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    setPage(0);
    fetchLogs();
  };

  const handleExport = () => {
    const csvContent = filteredLogs
      .map((log) => {
        const escapedMessage = log.message?.replace(/"/g, '""') || "";
        const timestamp = formatTimestamp(log.timestamp);
        return `"${timestamp}","${log.level}","${escapedMessage}"`;
      })
      .join("\\n");

    const blob = new Blob([`Timestamp,Level,Message\\n${csvContent}`], {
      type: "text/csv",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `logs-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    try {
      return new Date(timestamp).toLocaleString("id-ID", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (error) {
      return timestamp;
    }
  };

  // Get unique log levels from current logs for dynamic filter options
  const getAvailableLogLevels = () => {
    const levels = logs
      .map((log) => normalizeLogLevel(log.level))
      .filter((level, index, arr) => level && arr.indexOf(level) === index)
      .sort();
    return levels;
  };

  const availableLogLevels = getAvailableLogLevels();

  return (
    <React.Fragment>
      <Helmet title="System Logs" />

      <Breadcrumbs aria-label="Breadcrumb" mb={4}>
        <Link component={NavLink} to="/private">
          Home
        </Link>
        <Link component={NavLink} to="/erp/logs">
          System Logs
        </Link>
      </Breadcrumbs>

      <Typography variant="h3" gutterBottom display="inline">
        System Logs
      </Typography>

      <Divider my={6} />

      <ControlsContainer>
        <TextField
          placeholder="Search logs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel>Log Level</InputLabel>
          <Select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            label="Log Level"
          >
            <MenuItem value="All">All Levels</MenuItem>
            {availableLogLevels.map((level) => (
              <MenuItem key={level} value={level}>
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          onClick={handleRefresh}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
        >
          Refresh
        </Button>

        <Button
          variant="outlined"
          color="primary"
          onClick={handleExport}
          startIcon={<ExportIcon />}
          disabled={filteredLogs.length === 0}
        >
          Export CSV ({filteredLogs.length})
        </Button>
      </ControlsContainer>

      {/* Show filter info */}
      {(searchTerm || levelFilter !== "All") && (
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            {filteredLogs.length} of {logs.length} logs shown
            {searchTerm && ` • Search: "${searchTerm}"`}
            {levelFilter !== "All" && ` • Level: ${levelFilter}`}
          </Typography>
        </Box>
      )}

      <StyledTableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Level</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={4}
                  >
                    <CircularProgress size={24} style={{ marginRight: 8 }} />
                    Loading logs...
                  </Box>
                </TableCell>
              </TableRow>
            ) : filteredLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {logs.length === 0
                      ? "No logs available"
                      : "No logs match your filters"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLogs.map((log, index) => (
                <TableRow key={`${log.timestamp}-${index}`} hover>
                  <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                  <TableCell>
                    <Chip
                      label={log.level}
                      color={getLogLevelColor(log.level)}
                      variant={getLogLevelVariant(log.level)}
                      size="small"
                    />
                  </TableCell>
                  <LogMessage title={log.message}>{log.message}</LogMessage>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => {
            const total = count !== -1 ? count : `more than ${to}`;
            return `${from}-${to} of ${total}`;
          }}
        />
      </StyledTableContainer>
    </React.Fragment>
  );
};

export default LogViewer;
